import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistSection } from '../../../../types';
import { useDroppable } from '@dnd-kit/core';
import OutfitPieceInput from './OutfitPieceInput';
import OutfitPieceChip from './OutfitPieceChip';
import { Plus, Icon } from '../../../ui/Icon';

/** The drop target of the pieces outside any moment. Group ids are uuids, so this can't collide. */
export const WHOLE_DAY_DROP_ID = 'whole-day';

/** A planned piece joined with its suitcase item: text and "packed?" live there. */
export interface ShownPiece {
  id: string;
  checklistItemId: string;
  text: string;
  checked: boolean;
}

interface Props {
  /** A group id, or WHOLE_DAY_DROP_ID: where a dragged piece lands. */
  dropId: string;
  draggable: boolean;
  pieces: ShownPiece[];
  sections: ChecklistSection[];
  defaultSectionId: string | null;
  /** Everything already on the day, grouped or not: a piece is on a day once. */
  dayItemIds: Set<string>;
  readOnly: boolean;
  onPick: (itemId: string) => Promise<void>;
  onCreate: (text: string, sectionId: string | null) => Promise<void>;
  onRemove: (pieceId: string) => void;
}

/** The pieces of a day, or of one group in it, plus the control to add one. */
export default function OutfitPieceList({
  dropId, draggable, pieces, sections, defaultSectionId, dayItemIds, readOnly, onPick, onCreate, onRemove,
}: Props) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: dropId, disabled: !draggable });

  return (
    <div
      ref={setNodeRef}
      className={`-mx-2 px-2 py-1 rounded-xl transition-colors ${isOver ? 'bg-teal-50 dark:bg-teal-900/20 ring-1 ring-teal-300 dark:ring-teal-700' : ''}`}
    >
      {pieces.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {pieces.map(piece => (
            <OutfitPieceChip key={piece.id} piece={piece} readOnly={readOnly} draggable={draggable} onRemove={onRemove} />
          ))}
        </ul>
      )}

      {!readOnly && (adding ? (
        <OutfitPieceInput
          sections={sections}
          defaultSectionId={defaultSectionId}
          excludeIds={dayItemIds}
          onPick={onPick}
          onCreate={onCreate}
          onClose={() => setAdding(false)}
        />
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 -ml-1.5 mt-1 text-xs text-slate-400 hover:text-teal-700 hover:bg-teal-50 dark:hover:text-teal-300 dark:hover:bg-teal-900/25 transition-colors cursor-pointer"
        >
          <Icon as={Plus} size={12} />
          {t('packing.outfits.addPiece')}
        </button>
      ))}
    </div>
  );
}
